import { stripWorksExport } from "@utils/worksReceivables";
import { useProjectActions, useProjectContext } from "@contexts/projects";
import { downloadCsv, downloadWorkbook } from "@utils/downloadCsv";
import { trackReportDownload } from "@utils/activityTracker";
import { ensureDateType } from "@utils/formatDate";
import { loadStorage, saveStorage } from "@utils/localStorageManager";
import { useCallback, useEffect, useRef, useState } from "react";
import { isAmpliCurveVisible, isCashFlowRawExportVisible } from "./financialProjection/cashFlowPlanning";
import ReportDownloadMenu from "./ReportDownloadMenu";
import { projectWbsRoots } from "@utils/wbsCode";
import { fullReportSheet, reportMonths, wbsReportFields, wbsReportRows } from "@utils/projectionReports";

// Curvas que o BE devolve por EAP e por mês (`/project/getReport`), na ordem do menu e das
// colunas do relatório geral completo.
const WBS_SERIES = [
  { name: "physical_financials", label: "Previsto" },
  { name: "disbursements", label: "Desembolso" },
  { name: "ampli_disbursements", label: "Previsão de desembolso" },
  { name: "paid_values", label: "Pago" },
  { name: "to_pay_values", label: "A pagar" },
];

const BUILDING_ITEM_TYPES = ['units', 'cells', 'subcells', 'steps', 'substeps', 'services'];

const formatDateToInput = (value) => {
  const date = ensureDateType(value);
  return date.toISOString().slice(0, 7);
}

const formatInputToDate = (value) => {
  const [year, month] = value.split("-");
  return new Date(Date.UTC(year, month - 1, 1))
}

const monthYearToIso = (value) => {
  if (typeof value !== "string") return null;
  const [month, year] = value.split("/");
  if (!month || !year) return null;
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, 1));
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

const DateRangeFilter = () => {
  const { activeProject = {}, filterDates, xlsxData = {}, selectedGraph, filterDocs = {} } = useProjectContext();
  const {
    _id: projectId,
    start_date,
    end_date,
    lastPayment,
    firstPayment,
  } = activeProject;
  const { fields = [], resources = [] } = stripWorksExport(xlsxData);
  const { changeDates, getReport, getAllBuildingItems, updateMaxDates } = useProjectActions();

  const [showReportButtons, setShowReportButtons] = useState(false);
  const [savedDates, setSavedDates] = useState({})
  const [reportError, setReportError] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const triggerRef = useRef(null);

  const lastDate = monthYearToIso(lastPayment?.date);
  const firstDate = monthYearToIso(firstPayment) || start_date;

  const availableStartDates = [
    firstDate,
    start_date
  ].filter(Boolean);
  const olderDate = availableStartDates.reduce((earliest, current) =>
    new Date(current) < new Date(earliest) ? current : earliest, availableStartDates[0]);
  const hasDateRange = Boolean(projectId && start_date && end_date && lastDate && olderDate && filterDates?.beginAt && filterDates?.endAt);

  const changeStart = useCallback(({ target: { value } }) => {
    if (value.length <= 0) return;
    const startDate = formatInputToDate(value);
    if (filterDocs.paidData.length > 0) {
    }
    saveStorage(`${projectId}_filterDates`, {
      beginAt: startDate,
      endAt: filterDates.endAt
    })
    changeDates(projectId, startDate, filterDates.endAt)
  }, [projectId, filterDates.endAt])

  const changeEnd = useCallback(({ target: { value } }) => {
    if (value.length <= 0) return;
    const endDate = formatInputToDate(value);
    saveStorage(`${projectId}_filterDates`, {
      beginAt: filterDates.beginAt,
      endAt: endDate
    })
    changeDates(projectId, filterDates.beginAt, endDate)
  }, [projectId, filterDates.beginAt])


  useEffect(() => {
    if (!hasDateRange) return;
    updateMaxDates(olderDate, lastDate)
    const saved = loadStorage(`${projectId}_filterDates`);
    if (saved) {
      setSavedDates(saved);
      changeDates(projectId, new Date(saved.beginAt), new Date(saved.endAt));
    }
  }, [projectId])

  // Todo download desta tela passa por aqui, para registrar qual relatório foi baixado.
  const trackDownload = (report, name) => {
    trackReportDownload(report, {
      format: "xlsx",
      label: name,
      beginAt: filterDates?.beginAt ? ensureDateType(filterDates.beginAt).toISOString() : null,
      endAt: filterDates?.endAt ? ensureDateType(filterDates.endAt).toISOString() : null,
    });
  }

  const downloadReport = (e, reportFields, items, name, report) => {
    trackDownload(report, name);
    downloadCsv(e, reportFields, items, name);
  }

  const isWbsSeriesVisible = (name) => isCashFlowRawExportVisible(name, activeProject.cashFlowPlanning)
    // spec "Exportação sem curva": a política Ampli sem curva disponível não pode
    // oferecer o relatório, ou o arquivo sairia com valores zerados (D10).
    && (name !== "ampli_disbursements" || isAmpliCurveVisible(activeProject));
  const visibleWbsSeries = WBS_SERIES.filter(({ name }) => isWbsSeriesVisible(name));

  const fetchReport = async (col) => {
    const res = await getReport(col);
    return !res || res.errorCode || res.code >= 400 ? null : res;
  }

  const downloadAdvancements = async (e) => {
    const res = await fetchReport("advancements");
    if (!res) return false;
    const items = res.items.flatMap(i => i.data.map(j => ({
      from: j.from,
      to: j.to,
      value: j.value,
      name: i.name
    })));

    const fields = [
      { key: 'name', label: "Nome" },
      { key: 'value', label: "Valor" },
      { key: 'from', label: "Movido de" },
      { key: 'to', label: "Movido para" },
    ]

    downloadReport(e, fields, items, "advancements", "projecao_advancements");
    return true;
  }

  const downloadWbsSeries = async (e, col) => {
    const res = await fetchReport(col);
    if (!res) return false;
    const allItems = await getAllBuildingItems(BUILDING_ITEM_TYPES);
    const months = reportMonths([res]);
    const rows = wbsReportRows(res.items, allItems.items, months, projectWbsRoots(activeProject));
    downloadReport(e, wbsReportFields(months), rows, col, `projecao_${col}`);
    return true;
  }

  const downloadFullReport = async (e) => {
    const reports = [];
    for (const series of visibleWbsSeries) {
      const res = await fetchReport(series.name);
      if (!res) return false;
      reports.push({ ...series, res });
    }
    const allItems = await getAllBuildingItems(BUILDING_ITEM_TYPES);
    const months = reportMonths(reports.map(({ res }) => res));
    const sheet = fullReportSheet(reports, allItems.items, months, projectWbsRoots(activeProject));

    trackDownload("projecao_relatorio_geral_completo", "all_by_wbs");
    downloadWorkbook(e, [sheet], "all_by_wbs");
    return true;
  }

  const handleDownload = async (col, e) => {
    e.preventDefault()
    setShowReportButtons(false);
    setReportError(false);

    if (col === 'all') {
      downloadReport(e, fields, resources, col, "projecao_relatorio_geral")
      triggerRef.current?.focus();
      return;
    }

    setDownloading(true);
    try {
      let ok;
      if (col === 'all_by_wbs') ok = await downloadFullReport(e);
      else if (col === 'advancements') ok = await downloadAdvancements(e);
      else ok = await downloadWbsSeries(e, col);
      if (!ok) setReportError(true);
    } catch {
      setReportError(true);
    } finally {
      setDownloading(false);
      // O botão volta a ficar habilitado no próximo render; o foco volta para ele depois.
      setTimeout(() => triggerRef.current?.focus(), 0);
    }
  }

  const isDirectDownload = selectedGraph === 'fluxGraph' || selectedGraph === 'balanceGraph';

  const handleReportButton = (e) => {
    e.preventDefault()
    if (isDirectDownload) {
      const col = selectedGraph === 'fluxGraph' ? "Desembolsos acumulados" : "Compromissos da obra";
      downloadReport(e, fields, resources, col, `grafico_${selectedGraph}`)
      return
    }
    setReportError(false);
    setShowReportButtons(!showReportButtons)
  }

  const closeMenu = useCallback(() => setShowReportButtons(false), []);

  const reportSections = [
    {
      title: "Por EAP, mês a mês",
      items: visibleWbsSeries.map(({ name, label }) => ({ name, label })),
    },
    {
      title: "Movimentações",
      items: isCashFlowRawExportVisible("advancements", activeProject.cashFlowPlanning)
        ? [{ name: "advancements", label: "Ajustes de desembolso" }]
        : [],
    },
    {
      title: "Relatório geral",
      items: [
        { name: "all", label: "Resumo por datas", description: "Totais de cada curva por mês, como no gráfico" },
        { name: "all_by_wbs", label: "Completo por EAP", description: "EAP completa com as curvas lado a lado, mês a mês" },
      ],
    },
  ];

  if (!hasDateRange) return null;

  return <form className="date-range">
    <h3>Periodo:</h3>
    <input
      type="month"
      required={true}
      min={formatDateToInput(olderDate)}
      max={formatDateToInput(filterDates.endAt)}
      value={formatDateToInput(filterDates.beginAt)}
      onChange={changeStart}
    />
    <span className="color-gray">-</span>
    <input
      type="month"
      required
      min={formatDateToInput(filterDates.beginAt)}
      max={formatDateToInput(lastDate)}
      value={formatDateToInput(filterDates.endAt)}
      onChange={changeEnd}
    />

    <div className="report-download">
      <button
        type="button"
        ref={triggerRef}
        className="button button-accent small report-download__trigger"
        onClick={handleReportButton}
        disabled={downloading}
        aria-haspopup={isDirectDownload ? undefined : "menu"}
        aria-expanded={isDirectDownload ? undefined : showReportButtons}
      >
        {downloading ? "Gerando relatório…" : "Baixar relatório"}
        {!isDirectDownload && !downloading ? (
          <i
            className={`icon icon-angle-down report-download__chevron${showReportButtons ? " open" : ""}`}
            aria-hidden={true}
          ></i>
        ) : null}
      </button>
      {reportError ? (
        <p className="report-download__error" role="alert">
          Não foi possível gerar o relatório. Tente novamente.
        </p>
      ) : null}
      {showReportButtons && (
        <ReportDownloadMenu
          sections={reportSections}
          onSelect={handleDownload}
          onClose={closeMenu}
          triggerRef={triggerRef}
        />
      )}
    </div>
  </form>
};

export default DateRangeFilter;
